import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Link,
  Typography,
} from "@mui/material";
import { CheckCircle, Error as ErrorIcon, Instagram } from "@mui/icons-material";
import ButtonOne from "../../common/normal-button";
import AppStepper from "../../common/app-stepper";
import { INSTAGRAM_ACCOUNT_DETAIL, INSTAGRAM_CONNECT_START, INSTAGRAM_CONNECT_TOKEN } from "../../config/urls";
import { errorMessage, formatDate } from "./shared";

// Connect wizard (spec 10.2): before you start → log in with Instagram (popup) → setting up → test it.

const STEPS = ["Before you start", "Log in with Instagram", "Setting up", "Test it"];

const InstagramConnectWizard = ({ open, onClose, reconnect, envTokenAvailable, request }) => {
  const [step, setStep] = useState(0);
  const [ready, setReady] = useState(false);
  const [login, setLogin] = useState({ state: "idle" }); // idle | waiting | blocked | error
  const [result, setResult] = useState(null); // { account_id, username, checklist }
  const [testDm, setTestDm] = useState(null);
  const startedAt = useRef(null);
  const popupRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setStep(reconnect ? 1 : 0);
    setReady(false);
    setLogin({ state: "idle" });
    setResult(null);
    setTestDm(null);
  }, [open, reconnect]);

  // The OAuth popup posts its result back to this window.
  useEffect(() => {
    if (!open) return undefined;
    const onMessage = (event) => {
      // Only trust the popup we opened (its page is served by the backend's public host).
      if (event.data?.source !== "instagram-oauth" || event.source !== popupRef.current) return;
      if (event.data.ok) {
        setResult({ account_id: event.data.account_id, checklist: event.data.checklist || [] });
        setStep(2);
      } else {
        setLogin({ state: "error", message: event.data.message, cancelled: event.data.cancelled });
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [open]);

  // Step 4: wait for the first DM to arrive through the webhook.
  useEffect(() => {
    if (step !== 3 || !result?.account_id || testDm) return undefined;
    const timer = setInterval(async () => {
      try {
        const account = await request("get", INSTAGRAM_ACCOUNT_DETAIL(result.account_id));
        const latest = account.latest_inbound;
        setResult((r) => ({ ...r, username: account.username }));
        if (latest && new Date(latest.created_at) >= startedAt.current) setTestDm(latest);
      } catch {
        // keep polling
      }
    }, 3000);
    return () => clearInterval(timer);
  }, [step, result?.account_id, testDm, request]);

  const startLogin = async () => {
    setLogin({ state: "waiting" });
    try {
      const { auth_url: url } = await request("post", INSTAGRAM_CONNECT_START, { account_id: reconnect?.id });
      const popup = window.open(url, "instagram-connect", "width=520,height=720");
      popupRef.current = popup;
      setLogin(popup ? { state: "waiting" } : { state: "blocked", url });
    } catch (error) {
      setLogin({ state: "error", message: errorMessage(error, "Could not start the Instagram login.") });
    }
  };

  const connectWithServerToken = async () => {
    setLogin({ state: "waiting" });
    try {
      const data = await request("post", INSTAGRAM_CONNECT_TOKEN, { account_id: reconnect?.id });
      setResult({ account_id: data.account.id, username: data.account.username, checklist: data.checklist });
      setStep(2);
    } catch (error) {
      setLogin({ state: "error", message: errorMessage(error, "Could not connect with the server token.") });
    }
  };

  const goTest = () => {
    startedAt.current = new Date();
    setStep(3);
  };

  const accountName = result?.username || result?.checklist?.[0]?.detail?.split(" ·")[0] || "your account";

  const renderStep = () => {
    if (step === 0) {
      return (
        <>
          <Typography variant="body2" mb={1.5}>Check these before you log in:</Typography>
          <Box component="ol" sx={{ pl: 2.5, mt: 0, "& li": { mb: 1 } }}>
            <Typography component="li" variant="body2">
              The account is an Instagram <b>Business</b> or <b>Creator</b> account. To switch: Instagram app › Settings
              and privacy › Account type and tools › Switch to professional account.
            </Typography>
            <Typography component="li" variant="body2">
              <b>Allow access to messages</b> is ON: Settings › Messages and story replies › Message controls ›
              Connected tools.
            </Typography>
            <Typography component="li" variant="body2">You can log in to this Instagram account.</Typography>
          </Box>
          <FormControlLabel
            control={<Checkbox checked={ready} onChange={(e) => setReady(e.target.checked)} />}
            label="I've done this"
          />
          <Box mt={2}><ButtonOne title="Continue" disabled={!ready} onClick={() => setStep(1)} /></Box>
        </>
      );
    }
    if (step === 1) {
      return (
        <>
          <Typography variant="body2" mb={2}>
            {reconnect
              ? `Log in as @${reconnect.username} to renew BotBridge's access.`
              : "A window opens where you log in to Instagram and approve BotBridge."}
          </Typography>
          {login.state === "waiting" && (
            <Box display="flex" alignItems="center" gap={1.5} mb={2}>
              <CircularProgress size={18} />
              <Typography variant="body2">Finish in the Instagram window…</Typography>
            </Box>
          )}
          {login.state === "blocked" && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Your browser blocked the popup. Allow popups for this site, or{" "}
              <Link
                component="button"
                onClick={() => {
                  popupRef.current = window.open(login.url, "instagram-connect", "width=520,height=720");
                  setLogin({ state: "waiting" });
                }}
              >
                open the Instagram login
              </Link>.
            </Alert>
          )}
          {login.state === "error" && (
            <Alert severity={login.cancelled ? "info" : "error"} sx={{ mb: 2 }}>{login.message}</Alert>
          )}
          <ButtonOne
            startIcon={<Instagram />}
            title={login.state === "idle" ? "Log in with Instagram" : "Try again"}
            onClick={startLogin}
          />
          {envTokenAvailable && (
            <Typography variant="caption" color="text.secondary" display="block" mt={2}>
              Developer:{" "}
              <Link component="button" variant="caption" onClick={connectWithServerToken}>
                connect with the server token (IG_ACCESS_TOKEN)
              </Link>
            </Typography>
          )}
        </>
      );
    }
    if (step === 2) {
      const allOk = result.checklist.every((c) => c.ok);
      return (
        <>
          {result.checklist.map((c) => (
            <Box key={c.key} display="flex" gap={1.25} alignItems="flex-start" py={0.75}>
              {c.ok ? <CheckCircle color="success" fontSize="small" /> : <ErrorIcon color="error" fontSize="small" />}
              <Box>
                <Typography variant="body2" fontWeight={500}>{c.label}</Typography>
                {c.detail && <Typography variant="caption" color="text.secondary">{c.detail}</Typography>}
              </Box>
            </Box>
          ))}
          {!allOk && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Connected, but some setup steps failed. Use Test connection on the account to retry.
            </Alert>
          )}
          <Box mt={2}><ButtonOne title="Continue" onClick={goTest} /></Box>
        </>
      );
    }
    return (
      <>
        <Typography variant="body2" mb={2}>
          From another Instagram account, send a DM to <b>@{accountName}</b>. It appears here when BotBridge receives it.
        </Typography>
        {testDm ? (
          <Alert icon={<CheckCircle />} severity="success">
            Received {testDm.username ? `from @${testDm.username}` : ""}: “{testDm.text || testDm.msg_type}” at{" "}
            {formatDate(testDm.created_at)}
          </Alert>
        ) : (
          <Box display="flex" alignItems="center" gap={1.5}>
            <CircularProgress size={18} />
            <Typography variant="body2">Waiting for a DM… If nothing arrives, check that Allow access to messages is ON.</Typography>
          </Box>
        )}
        <Box mt={2} display="flex" gap={1.5}>
          <ButtonOne title="Done" onClick={() => onClose(true)} />
          {!testDm && <ButtonOne variant="outlined" title="Skip for later" onClick={() => onClose(true)} />}
        </Box>
      </>
    );
  };

  return (
    <Dialog open={open} onClose={() => onClose(step >= 2)} maxWidth="sm" fullWidth>
      <DialogTitle>{reconnect ? `Reconnect @${reconnect.username}` : "Connect Instagram"}</DialogTitle>
      <DialogContent>
        <Box mb={3} mt={1}><AppStepper steps={STEPS} activeStep={step} /></Box>
        {renderStep()}
      </DialogContent>
    </Dialog>
  );
};

export default InstagramConnectWizard;
